import { usersApiService } from '../../usersApi';
import { usernameCacheService } from '../../usernameCache';
import { elasticsearchService } from '../../elasticsearchService';
import { fetchTesseractStaffList } from '../../tesseractStaffApi';
import { staffManagerApiService, StaffManagerApiError } from '../../staffManagerApi';
import { dataControllerApiService, DataControllerApiError, type TesseractSubjectExport } from '../../dataControllerApi';
import { staffApiService, StaffApiError } from '../../staffApi';
import type { PlayerStats } from '../../../types/player';
import { SkipSource, autoSection, fieldsSection, isNotFound, isSameUuid, jsonSection, type SourceDefinition } from '../helpers';

/**
 * Phase 1 — identity & account sources. These also enrich the subject (username, Discord ID,
 * email, Tesseract id, IPs, aliases) so the phase-2 sources can match on every identifier.
 */

interface PlayerSearchHit {
  _id: string;
  _source: { uuid?: string; lastKnownName?: string; nickname?: string; lastKnownIp?: string };
}

export const usersApiSource: SourceDefinition = {
  id: 'users-api.account',
  title: 'Network account & statistics',
  system: 'Users API (api.playcdu.co)',
  category: 'identity',
  phase: 1,
  description: 'The network account record: username, linked Discord account, ban flag, first and last join, playtime, servers seen, gameplay counters (blocks, deaths, distances, kills) and awards.',
  run: async (ctx) => {
    let stats: PlayerStats;
    try {
      stats = (await usersApiService.getPlayerStatsByUuid(ctx.uuid)).player_stats;
    } catch (e) {
      if (isNotFound(e)) return { sections: [], recordCount: 0 };
      throw e;
    }
    if (stats.username) {
      ctx.subject.username = ctx.subject.username ?? stats.username;
      ctx.addAlias(stats.username);
      usernameCacheService.seed(ctx.uuid, stats.username);
    }
    if (stats.discordid && /^\d{17,20}$/.test(String(stats.discordid))) ctx.subject.discordId = ctx.subject.discordId ?? String(stats.discordid);

    const { player_awards, ...scalars } = stats;
    const mc = Object.fromEntries(Object.entries(scalars).filter(([k]) => k.startsWith('mc_')));
    const account = Object.fromEntries(Object.entries(scalars).filter(([k]) => !k.startsWith('mc_')));
    const sections = [
      fieldsSection('Account', account, {
        first: ['username', 'uuid', 'discordid', 'banned', 'firstjoinedtime', 'firstjoinedserverid', 'lastjoinedtime', 'lastjoinedservername', 'playtime', 'uniqueserversseenon', 'isserverbooster', 'votes', 'numberofnotes', 'numberofwarns'],
        formats: { discordid: 'mono', uuid: 'mono', banned: 'bool', isserverbooster: 'bool', firstjoinedtime: 'datetime', lastjoinedtime: 'datetime', playtime: 'text' },
        labels: { discordid: 'Discord ID', firstjoinedtime: 'First joined', firstjoinedserverid: 'First joined server', lastjoinedtime: 'Last joined', lastjoinedservername: 'Last joined server', uniqueserversseenon: 'Unique servers seen on', isserverbooster: 'Server booster', numberofwarns: 'Warnings (count)', numberofnotes: 'Notes (count)' },
      }),
      autoSection('Gameplay counters', Object.entries(mc).map(([stat, value]) => ({ stat: stat.replace(/^mc_/, ''), value })), { as: 'table', formats: { value: 'number' } }),
    ];
    if (Array.isArray(player_awards) && player_awards.length > 0) {
      sections.push(autoSection('Awards', player_awards as unknown as Record<string, unknown>[], { as: 'table', omit: ['img_url', 'emoji'] }));
    }
    return { sections, recordCount: 1 };
  },
};

export const playerSearchIndexSource: SourceDefinition = {
  id: 'es.players',
  title: 'Player search index entry',
  system: 'Elasticsearch (cdu-players-v1)',
  category: 'identity',
  phase: 1,
  description: 'The staff search index document for this player: last known name, nickname and last known IP address. This index lets staff look a player up by IP.',
  run: async (ctx) => {
    const result = (await elasticsearchService.searchPlayers(ctx.uuid)) as { hits?: { hits?: PlayerSearchHit[] } };
    const hits = (result.hits?.hits ?? []).filter((h) => isSameUuid(h._source?.uuid, ctx.uuid));
    if (hits.length === 0) return { sections: [], recordCount: 0 };
    for (const h of hits) {
      ctx.addIps([h._source.lastKnownIp]);
      ctx.addAlias(h._source.lastKnownName);
      ctx.addAlias(h._source.nickname);
      if (!ctx.subject.username && h._source.lastKnownName) ctx.subject.username = h._source.lastKnownName;
    }
    return {
      sections: [autoSection('Index documents', hits.map((h) => ({ document_id: h._id, ...h._source })), { as: 'table', formats: { lastKnownIp: 'mono', uuid: 'mono', document_id: 'mono' } })],
      recordCount: hits.length,
    };
  },
};

const IP_COLUMNS = ['ip_address', 'referrer_ip_address', 'referred_ip_address'];

export const tesseractAccountSource: SourceDefinition = {
  id: 'tesseract.account',
  title: 'Tesseract (craftdownunder.co) account',
  system: 'Tesseract (craftdownunder.co)',
  category: 'identity',
  phase: 1,
  description:
    "The player's website account as held by Tesseract: email, verification state, sign-in sessions with IP addresses and device fingerprints, linked Minecraft and Discord identities, roles, groups, staff teams, bans, donations, awards, referrals, training records, group finder activity, guild records and previous deletion requests. Secrets (passwords, tokens, codes) are never included.",
  run: async (ctx) => {
    let data: TesseractSubjectExport;
    try {
      data = await dataControllerApiService.getTesseractSubject(ctx.uuid, { email: ctx.subject.email, discord_id: ctx.subject.discordId, tesseract_id: ctx.subject.tesseractId });
    } catch (e) {
      if (e instanceof DataControllerApiError && (e.status === 403 || e.status === 502 || e.status === 503)) throw new SkipSource(`Tesseract not readable by the exporting user (${e.message})`);
      throw e;
    }
    const notes: string[] = [];
    const sections = [];
    const account = data.account as { id?: string; email?: string; username?: string } | null;
    if (account) {
      ctx.subject.email = ctx.subject.email ?? account.email ?? null;
      ctx.subject.tesseractId = ctx.subject.tesseractId ?? account.id ?? null;
      ctx.subject.tesseractUsername = ctx.subject.tesseractUsername ?? account.username ?? null;
      const discord = data.discord as { discord_id?: string; discord_username?: string } | null;
      if (discord) {
        ctx.subject.discordId = ctx.subject.discordId ?? discord.discord_id ?? null;
        ctx.subject.discordUsername = ctx.subject.discordUsername ?? discord.discord_username ?? null;
      }
      const minecraft = data.minecraft as { minecraft_username?: string } | null;
      ctx.addAlias(minecraft?.minecraft_username);
      sections.push(fieldsSection('Account', account as Record<string, unknown>, { first: ['id', 'username', 'email'], formats: { id: 'mono', email_verified: 'bool', disabled: 'bool', donation_privacy: 'bool', created_at: 'datetime-ms', updated_at: 'datetime-ms', last_login: 'datetime-ms', last_group_sync_at: 'datetime-ms' } }));
      if (minecraft) sections.push(fieldsSection('Linked Minecraft account', minecraft as Record<string, unknown>, { formats: { minecraft_uuid: 'mono', linked_at: 'datetime-ms', updated_at: 'datetime-ms' } }));
      if (discord) sections.push(fieldsSection('Linked Discord account', discord as Record<string, unknown>, { formats: { discord_id: 'mono', linked_at: 'datetime-ms', updated_at: 'datetime-ms' } }));
      if (data.matched_by && data.matched_by !== 'minecraft') notes.push(`The website account was matched by ${data.matched_by === 'user_id' ? 'Tesseract user id' : data.matched_by === 'discord' ? 'the linked Discord account' : data.matched_by}, not by a Minecraft link${data.minecraft ? '' : ' (the account has no Minecraft account linked)'}.`);
    } else {
      notes.push('No website account matched the Minecraft UUID, Discord ID or email known for this player. Rows keyed by the Minecraft UUID (group finder, guilds, pending awards) are still listed when held.');
    }
    let rows = 0;
    for (const ds of data.datasets) {
      if (ds.error) notes.push(`${ds.title}: could not be read (${ds.error}).`);
      if (ds.rows.length === 0) continue;
      rows += ds.rows.length;
      for (const r of ds.rows) ctx.addIps(IP_COLUMNS.map((c) => (typeof r[c] === 'string' ? (r[c] as string) : null)));
      sections.push(autoSection(ds.title, ds.rows, { description: ds.description, total: ds.truncated ? ds.total : undefined }));
    }
    return { sections, recordCount: (account ? 1 : 0) + rows, notes: notes.length ? notes : undefined };
  },
};

export const tesseractPublicStaffSource: SourceDefinition = {
  id: 'tesseract.public-staff',
  title: 'Public staff roster listing',
  system: 'Tesseract public API',
  category: 'identity',
  phase: 2,
  description: 'Whether the player appears on the public staff roster (Minecraft UUID and staff teams).',
  run: async (ctx) => {
    const staff = await fetchTesseractStaffList();
    const entry = staff.find((s) => isSameUuid(s.minecraft_uuid, ctx.uuid));
    if (!entry) return { sections: [], recordCount: 0 };
    return { sections: [fieldsSection('Roster entry', { minecraft_uuid: entry.minecraft_uuid, teams: entry.teams }, { formats: { minecraft_uuid: 'mono' } })], recordCount: 1 };
  },
};

export const stewardProfileSource: SourceDefinition = {
  id: 'steward.profile',
  title: 'Steward behavioural staff profile',
  system: 'Steward bot (api.playcdu.co)',
  category: 'staff',
  phase: 2,
  description: 'The automatically generated staff profile the steward bot maintains from ticket activity: evidence summary, active-hours schedule, quoted citations, strengths, weaknesses, routing advice and reliability signals.',
  run: async (ctx) => {
    if (!ctx.subject.discordId) throw new SkipSource('no Discord ID is known for this player');
    try {
      const profile = await staffApiService.getProfile(ctx.subject.discordId, { force: true });
      const { citations, profile: details, schedule, evidence, ...head } = profile;
      const sections = [
        fieldsSection('Profile record', head, { formats: { built_at: 'datetime-s', generated_at: 'datetime-s', discord_id: 'mono' } }),
        fieldsSection('Evidence summary', evidence as unknown as Record<string, unknown>, { formats: { oldest_at: 'datetime-s', newest_at: 'datetime-s' } }),
        fieldsSection('Activity schedule (UTC)', schedule as unknown as Record<string, unknown>),
      ];
      const citationRows = Object.values(citations ?? {}) as unknown as Record<string, unknown>[];
      if (citationRows.length) sections.push(autoSection('Citations (quoted evidence)', citationRows, { as: 'records', formats: { observed_at: 'datetime-s' } }));
      if (details) sections.push(jsonSection('Assessment', details, 'The full generated assessment, verbatim.'));
      return { sections, recordCount: 1 + citationRows.length };
    } catch (e) {
      if (e instanceof StaffApiError && e.status === 404) return { sections: [], recordCount: 0 };
      if (e instanceof StaffApiError && (e.status === 403 || e.status === 503)) throw new SkipSource(e.message);
      throw e;
    }
  },
};

export const stewardTicketsSource: SourceDefinition = {
  id: 'steward.tickets',
  title: 'Steward ticket counts & honours',
  system: 'Steward bot (api.playcdu.co)',
  category: 'staff',
  phase: 2,
  description: 'Tickets closed per month (last six months), MVP and raffle standings as reported by the steward bot.',
  run: async (ctx) => {
    if (!ctx.subject.discordId) throw new SkipSource('no Discord ID is known for this player');
    try {
      const counts = await staffApiService.getTicketCounts({ force: true });
      const person = counts.staff.find((p) => p.discord_id === ctx.subject.discordId);
      if (!person) return { sections: [], recordCount: 0 };
      const { months, ...head } = person as unknown as { months: Record<string, Record<string, unknown>> } & Record<string, unknown>;
      const monthRows = Object.entries(months ?? {}).map(([month, standing]) => ({ month, ...standing }));
      return {
        sections: [fieldsSection('Person', head, { formats: { discord_id: 'mono' } }), autoSection('Monthly standings', monthRows, { as: 'table' })],
        recordCount: 1 + monthRows.length,
      };
    } catch (e) {
      if (e instanceof StaffApiError && (e.status === 403 || e.status === 503)) throw new SkipSource(e.message);
      throw e;
    }
  },
};

export const staffMemberSummarySource: SourceDefinition = {
  id: 'staff.member-summary',
  title: 'Staff Manager member summary',
  system: 'Staff Manager',
  category: 'staff',
  phase: 2,
  description: 'The staff summary held for staff members: staff-since date, tenure, moderation activity authored in the last 90 days, staff panel action counts, and the Staff Manager Notes summary.',
  run: async (ctx) => {
    if (!ctx.subject.discordId) throw new SkipSource('no Discord ID is known for this player');
    try {
      const summary = await staffManagerApiService.getMember(ctx.subject.discordId, { uuid: ctx.uuid });
      const { tesseract, activity, staff_notes, ...head } = summary;
      const sections = [
        fieldsSection('Summary', { ...head, staff_notes_summary: staff_notes }, { formats: { staff_since: 'datetime-ms', generated_at: 'datetime-ms', discord_id: 'mono' } }),
        jsonSection('Activity (90 days)', activity),
      ];
      if (tesseract) sections.push(jsonSection('Tesseract entry (as returned)', tesseract));
      return { sections, recordCount: 1 };
    } catch (e) {
      if (e instanceof StaffManagerApiError && (e.status === 403 || e.status === 503 || e.status === 502)) throw new SkipSource(e.message);
      throw e;
    }
  },
};
